import { Auth0Provider } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { Toaster } from 'src/shared/components/ui/toaster';
import { BreadcrumbProvider } from 'src/shared/contexts/BreadcrumbContext';
import { RadixPointerEventsCleanup } from 'src/shared/contexts/RadixPointerEventsCleanup';
import { useClearCache } from 'src/shared/hooks/useClearCache';
import { useInitialRender } from 'src/shared/hooks/useInitialRender';
import mixpanel from 'src/shared/lib/mixpanel';
import { ReactChildren } from 'src/shared/types/util.types';

// this stops recharts from bitching about typescript
const error = console.error;
console.error = (...args: any) => {
  if (/defaultProps/.test(args[0])) return;
  error(...args);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        // Default no-op queryFn for queries used as state stores
        return {};
      },
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: import.meta.env.MODE !== 'development' ? window?.localStorage : null,
  key: 'tekstai-cache',
});

export default function RootProviders({ children }: ReactChildren) {
  useClearCache();

  useInitialRender(() => {
    if (import.meta.env.MODE !== 'development') {
      datadogLogs.init({
        clientToken: 'pubce53bcdc8a3b5a12c57ce679ddf04c6d',
        site: 'datadoghq.eu',
        service: 'frontend',
        env: import.meta.env.MODE,
        version: import.meta.env.VITE_GIT_SHA || 'local',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });

      datadogRum.init({
        applicationId: 'be00ac33-6984-4ea4-979b-1078a5015475',
        clientToken: 'pubce53bcdc8a3b5a12c57ce679ddf04c6d',
        site: 'datadoghq.eu',
        service: 'frontend',
        env: import.meta.env.MODE,
        version: import.meta.env.VITE_GIT_SHA || 'local',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
        beforeSend: (event) => (event.device && event.device.name !== 'HeadlessChrome') || !!event.error,
        allowedTracingUrls: [(url) => (url.includes('tekst.ai') || url.includes('tekst.com')) && !url.includes('auth')],
      });

      datadogRum.startSessionReplayRecording();
      if (import.meta.env.VITE_MIXPANEL_TOKEN) {
        mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
          api_host: 'https://api-eu.mixpanel.com',
          persistence: 'localStorage',
          autocapture: true,
          autotrack: true,
          record_sessions_percent: 100,
          record_idle_timeout_ms: 600000,
          record_mask_text_selector: '',
          record_block_selector: '',
        });
      }
    }
  }, []);

  if (!import.meta.env.VITE_AUTH0_BASE_URL || !import.meta.env.VITE_AUTH0_CLIENT_ID) {
    throw new Error('Missing Auth0 environment variables');
  }

  return (
    <>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_BASE_URL}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: typeof window !== 'undefined' ? window.location.origin : '',
          audience: 'http://localhost:8000',
        }}
        cacheLocation="localstorage"
      >
        <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
          <RadixPointerEventsCleanup>
            <BreadcrumbProvider>
              <TooltipProvider>{children}</TooltipProvider>
            </BreadcrumbProvider>
          </RadixPointerEventsCleanup>
          <ReactQueryDevtools position="right" initialIsOpen={false} />
        </PersistQueryClientProvider>
      </Auth0Provider>
      <Toaster />
    </>
  );
}
